import React, { Component, Fragment } from 'react';
import injectSheet from 'react-jss';
import { Link } from 'react-router-dom-v5-compat';
import SortableTree from 'react-sortable-tree';

import ActionsMenu from '~/components/Shared/ActionsMenu/ActionsMenu';
import { Markdown } from '~/eds/src/components/Markdown/Markdown';
import { Modal } from '~/eds/src/components/Modal/Modal';
import { injectIntl } from 'react-intl';

import { black4 } from '../../../assets/shared-styles/general';
import {
  MODAL_DELETE,
  MODAL_DOCUMENT_GROUP_RELATION,
} from '../../../types/modal.types';
import { SUCCESS, WARNING } from '../../../types/toast.types';
import {
  deleteDocumentFromGroup,
  editDocumentRelationshipGroup,
} from '../../DocumentGroupsPage/DocumentGroupsPage.data';
import { documentTypeIcon } from '../../DocumentsPage/Document.utils';
import { showToast } from '../../Shared/EcToast/showToast';
import DocumentFilledIcon from '../../Shared/Icons/DocumentFilledIcon';
import LinkIcon from '../../Shared/Icons/LinkIcon';
import EcMultipleLocationsLink from '../EcMultipleLocationsLink/EcMultipleLocationsLink';
import styles from './DocumentTree.styles';

const maxDepth = 5;

const customSearchMethod = ({ node, searchQuery }) =>
  searchQuery &&
  node.title.toLowerCase().indexOf(searchQuery.toLowerCase()) > -1;

class DocumentTree extends Component {
  constructor(props) {
    super(props);

    this.handleShowModal = this.handleShowModal.bind(this);
    this.handleHideModal = this.handleHideModal.bind(this);
    this.onDocumentRemoveFromGroup = this.onDocumentRemoveFromGroup.bind(this);

    this.state = {
      primaryModal: null,
      searchString: this.props.searchString,
      treeData: this.props.treeData,
      preparing: true,
    };
  }

  componentDidMount() {
    this.setUpWidth();
  }

  componentDidUpdate() {
    this.setUpWidth();
  }

  setUpWidth() {
    const elements = Array.from(
      document.getElementsByClassName('rst__nodeContent'),
    );

    elements.forEach((e) => {
      e.style = `left: ${e.style.left} ; width: calc(95% - ${e.style.left})`;
    });

    setTimeout(() => {
      this.setState({ preparing: false });
    }, 200);
  }

  handleTreeOnChange = (treeData) => {
    this.setState({ treeData });
  };

  handleSearchOnChange = (searchString) => {
    this.setState({ searchString });
  };

  handleMasterDocumentChange(node, isMaster) {
    const { reloadDocumentTreeData, intl } = this.props;
    editDocumentRelationshipGroup(node.docGroupId, {
      relationship: 'linked',
      isMaster,
    })
      .then(() => {
        showToast(
          SUCCESS,
          isMaster
            ? intl.formatMessage({
                id: 'DocumentTree.documentMarkedAsMaster',
                defaultMessage:
                  'The document has been successfully marked as Master.',
                description:
                  'Success message when document is marked as master',
              })
            : intl.formatMessage({
                id: 'DocumentTree.documentUnmarkedAsMaster',
                defaultMessage:
                  'The document has been successfully unmarked as Master.',
                description:
                  'Success message when document is unmarked as master',
              }),
        );
        reloadDocumentTreeData();
      })
      .catch(() =>
        showToast(
          WARNING,
          isMaster
            ? intl.formatMessage({
                id: 'DocumentTree.errorMarkingDocument',
                defaultMessage:
                  'An error occurred while marking the document in this group.',
                description:
                  'Error message when marking document as master fails',
              })
            : intl.formatMessage({
                id: 'DocumentTree.errorUnmarkingDocument',
                defaultMessage:
                  'An error occurred while unmarking the document in this group.',
                description:
                  'Error message when unmarking document as master fails',
              }),
        ),
      );
  }

  onDocumentRemoveFromGroup() {
    const { reloadDocumentTreeData, intl } = this.props;
    const { selectedItem } = this.state;
    deleteDocumentFromGroup(selectedItem.node.docGroupId)
      .then(() => {
        showToast(
          SUCCESS,
          intl.formatMessage({
            id: 'DocumentTree.removeDocumentSuccess',
            defaultMessage: 'Document has been removed from this group.',
            description: 'Success message when document is removed from group',
          }),
        );
        reloadDocumentTreeData();
      })
      .catch(() =>
        showToast(
          WARNING,
          intl.formatMessage({
            id: 'DocumentTree.removeDocumentError',
            defaultMessage:
              'An error occurred while removing the document from this group.',
            description:
              'Error message when removing document from group fails',
          }),
        ),
      );
  }

  handleShowModal(modal, selectedItem = null) {
    this.setState({
      primaryModal: modal,
      selectedItem,
    });
  }

  handleHideModal() {
    this.setState({ primaryModal: null, selectedItem: null });
  }

  getMenuItems = (item) => {
    const { intl } = this.props;
    const items = [];

    items.push(
      {
        content: item.node.isMaster
          ? intl.formatMessage({
              id: 'DocumentTree.unmarkAsMaster',
              defaultMessage: 'Unmark as "Master"',
              description: 'Menu item to unmark document as master',
            })
          : intl.formatMessage({
              id: 'DocumentTree.markAsMaster',
              defaultMessage: 'Mark as "Master"',
              description: 'Menu item to mark document as master',
            }),
        onClick: () =>
          this.handleMasterDocumentChange(
            item.node,
            item.node.isMaster ? false : true,
          ),
      },
      {
        content: intl.formatMessage({
          id: 'DocumentTree.removeFromGroup',
          defaultMessage: 'Remove from Group',
          description: 'Menu item to remove document from group',
        }),
        onClick: () => this.handleShowModal(MODAL_DELETE, item),
      },
    );

    return items;
  };

  renderExtraOptions(item) {
    const { classes, intl } = this.props;

    return (
      <div className={classes.actionsWrapper}>
        <ActionsMenu
          id={`document_tree_item_actions_menu?item=${item.node.documentId}`}
          items={this.getMenuItems(item)}
          align="end"
          smallIcon
          title={intl.formatMessage({
            id: 'DocumentTree.editDocumentGroupMenuTitle',
            defaultMessage: 'edit document group document menu',
            description: 'Title for document group actions menu',
          })}
        />
      </div>
    );
  }

  renderDeleteModal() {
    const { intl } = this.props;
    return (
      <Modal
        title={intl.formatMessage({
          id: 'DocumentTree.removeDocumentModalTitle',
          defaultMessage: 'Remove Document from Group?',
          description: 'Title for remove document confirmation modal',
        })}
        onHide={this.handleHideModal}
        onCancel={this.handleHideModal}
        isVisible
        children={
          <Markdown
            text={intl.formatMessage(
              {
                id: 'DocumentTree.removeDocumentConfirmation',
                defaultMessage:
                  "Are you sure you want to remove {documentTitle} from this group? You won't be able to undo this action.",
                description:
                  'Confirmation message for removing document from group. Example: documentTitle = "Contract Agreement"',
              },
              {
                documentTitle: this.state.selectedItem.node.title,
              },
            )}
          />
        }
        primaryAction={{
          text: intl.formatMessage({
            id: 'DocumentTree.deleteButtonText',
            defaultMessage: 'Delete',
            description: 'Delete button text in confirmation modal',
          }),
          onClick: () => {
            this.onDocumentRemoveFromGroup();
            this.handleHideModal();
          },
          variant: 'danger',
        }}
      />
    );
  }

  renderDocumentTitle(rowInfo) {
    const { classes } = this.props;
    return rowInfo.node.handlers.length ? (
      rowInfo.node.handlers.length > 1 ? (
        <EcMultipleLocationsLink
          wrapperStyles={classes.groupDocMultipleLocation}
          documentName={rowInfo.node.title}
          documentHandlers={rowInfo.node.handlers}
        >
          {documentTypeIcon(rowInfo.node.fileType)}
          <span className={classes.groupDocumentName}>
            {rowInfo.node.title}
          </span>
        </EcMultipleLocationsLink>
      ) : (
        <Link to={`/document/${rowInfo.node.handlerIds[0]}`}>
          {documentTypeIcon(rowInfo.node.fileType)}
          <span className={classes.groupDocumentName}>
            {rowInfo.node.title}
          </span>
        </Link>
      )
    ) : (
      <div className={classes.groupDocMultipleLocation}>
        {documentTypeIcon(rowInfo.node.fileType)}
        <span className={classes.groupDocumentName}>{rowInfo.node.title}</span>
      </div>
    );
  }

  render() {
    const { classes, handleShowModal, searchString, treeId, allowEdit, intl } =
      this.props;
    const { preparing, treeData, primaryModal } = this.state;

    if (preparing) {
      return null;
    }
    return (
      <div>
        <div className={classes.treeWrapper} id={treeId}>
          {treeData ? (
            <SortableTree
              treeData={treeData}
              onChange={this.handleTreeOnChange}
              canDrag={false}
              canDrop={() => false}
              maxDepth={maxDepth}
              searchMethod={customSearchMethod}
              searchQuery={searchString}
              isVirtualized={false}
              generateNodeProps={(rowInfo) => ({
                title: rowInfo.node.isVisible ? (
                  <span className={classes.groupDocumentItem}>
                    <span className={classes.groupDocumentDot} />
                    {this.renderDocumentTitle(rowInfo)}
                    {rowInfo.node.isMaster ? (
                      <span className={classes.masterTag}>
                        {intl.formatMessage({
                          id: 'DocumentTree.masterTag',
                          defaultMessage: 'Master',
                          description: 'Tag text for master document',
                        })}
                      </span>
                    ) : null}
                  </span>
                ) : (
                  <span className={classes.groupDocumentItem}>
                    <span className={classes.groupDocumentDot} />
                    <span>
                      <DocumentFilledIcon color={black4} size="20" />
                    </span>
                    <span className={classes.groupDocumentNameInvisible}>
                      {intl.formatMessage({
                        id: 'DocumentTree.noPermissionMessage',
                        defaultMessage:
                          "You don't have the necessary permissions to view this document.",
                        description:
                          'Message shown when user lacks permission to view document',
                      })}
                    </span>
                  </span>
                ),
                buttons: [
                  allowEdit && rowInfo.node.isVisible ? (
                    <Fragment>
                      <button
                        title={intl.formatMessage({
                          id: 'DocumentTree.editDocumentRelationshipTitle',
                          defaultMessage: 'Edit document relationship',
                          description:
                            'Title for edit document relationship button',
                        })}
                        className={classes.treeIcon}
                        onClick={() =>
                          handleShowModal(
                            MODAL_DOCUMENT_GROUP_RELATION,
                            rowInfo,
                          )
                        }
                      >
                        <LinkIcon />
                      </button>
                      <span className={classes.treeIcon}>
                        {this.renderExtraOptions(rowInfo)}
                      </span>
                    </Fragment>
                  ) : null,
                ],
              })}
            />
          ) : null}
          {primaryModal === MODAL_DELETE && this.renderDeleteModal()}
        </div>
      </div>
    );
  }
}

export default injectIntl(injectSheet(styles)(DocumentTree));
